import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Button, CssBaseline, IconButton, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';

import { redColors } from 'theme/colors';
import { glassPanelSx } from 'theme';

import Logo from './Logo';
import MobileDrawer from './MobileDrawer';
import { getCursor, getDisableRipple, getMarginBottom, navItems } from './utils';

const PHONE_NUMBER = '0452393591';
const PHONE_NUMBER_DISPLAY = '045 239 3591';
const PRIMARY_COLOR = 'primary.main';
const LIGHT_TEXT_COLOR = 'typography.light';
const PHONE_GRADIENT = `linear-gradient(90deg, ${redColors.light}, ${redColors.dark})`;

const NavBar: React.FC = () => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const handleDrawerToggle = () => setIsOpen((prevState) => !prevState);

  return (
    <>
      <CssBaseline />
      <Box
        component='nav'
        sx={{
          width: '100%',
          position: 'sticky',
          top: { xs: 10, sm: 16 },
          zIndex: 20,
          mb: getMarginBottom(isMdDown),
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            ...glassPanelSx,
            borderRadius: '16px',
            px: { xs: 1.5, sm: 2.5 },
            boxShadow: '0 12px 30px -14px rgba(0,0,0,0.6)',
          }}
        >
          <IconButton
            aria-label='Avaa valikko'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{
              display: {
                xs: 'flex',
                md: 'none',
              },
              mr: 1,
              ml: -0.5,
            }}
          >
            <MenuIcon sx={{ color: LIGHT_TEXT_COLOR }} />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title='Etusivu'
              arrow
              placement='bottom-start'
              componentsProps={{
                tooltip: {
                  sx: {
                    ...glassPanelSx,
                    color: LIGHT_TEXT_COLOR,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                    px: 1.25,
                    py: 0.6,
                    borderRadius: '8px',
                  },
                },
                arrow: {
                  sx: { color: 'rgba(36,38,42,0.78)' },
                },
              }}
            >
              <Box sx={{ display: 'inline-flex' }}>
                <Logo />
              </Box>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: {
                xs: 'none',
                md: 'flex',
              },
              alignItems: 'center',
              gap: 2,
            }}
          >
            {navItems.map(({ name, path }) => {
              const isSelected = path === pathname;

              return (
                <Button key={name} component={Link} to={path} disableRipple={getDisableRipple(isSelected)}>
                  <Typography
                    variant='p3'
                    sx={{
                      color: LIGHT_TEXT_COLOR,
                      cursor: getCursor(isSelected),
                      opacity: isSelected ? 1 : 0.65,
                      pb: '4px',
                      borderBottom: '2px solid',
                      borderBottomColor: isSelected ? PRIMARY_COLOR : 'transparent',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {name}
                  </Typography>
                </Button>
              );
            })}
          </Box>
          <Button
            href={`tel:${PHONE_NUMBER}`}
            aria-label={`Soita ${PHONE_NUMBER_DISPLAY}`}
            disableRipple
            sx={{
              ml: { xs: 'auto', sm: 2 },
              px: { xs: 1.75, sm: 2.25 },
              py: 1,
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: { xs: 0, lg: 1 },
              borderRadius: 999,
              background: PHONE_GRADIENT,
              color: LIGHT_TEXT_COLOR,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              boxShadow: `0 0 30px -8px ${redColors.dark}`,
              '&:hover': { background: PHONE_GRADIENT },
            }}
          >
            <PhoneIcon sx={{ fontSize: 16 }} />
            <Typography variant='p3b' sx={{ display: { xs: 'none', lg: 'block' } }}>
              {PHONE_NUMBER_DISPLAY}
            </Typography>
          </Button>
        </Toolbar>
      </Box>
      <MobileDrawer isOpen={isOpen} onClose={handleDrawerToggle} />
    </>
  );
};

export default NavBar;
